import React, { useState } from 'react';
import useOrderSummary, {
  OrderSummary,
} from '../hooks/reactQuery/useOrderSummary';
import pagesSlug from '../constant/pages-slug';
import useUrlFilters from '../hooks/useUrlFilters';
import { initialParams } from '../hooks/useListPage';
import CalendarFilter from './TableFilters/CalendarFilter';
import { formatDateRange } from '../utils/formatOrderDate';
import {
  DEFAULT_RANGE,
  RANGE_PRESETS,
  SummaryRange,
  parseRange,
  priorDelta,
  priorLabel,
  rangeTitle,
  rangeToParam,
} from '../utils/summaryRange';

interface Counts {
  [key: string]: number;
}

interface Tile {
  key: string;
  label: string;
  value?: number | null;
  sub?: React.ReactNode;
  warn?: boolean;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// "97% on time" when anything was delivered; otherwise how the count compares to before.
const deliveredSub = (
  delivered: OrderSummary['delivered'] | undefined,
  days: number,
): React.ReactNode => {
  if (!delivered) return null;
  if (delivered.onTimePercent != null) {
    return (
      <>
        <b>{delivered.onTimePercent}%</b> on time
      </>
    );
  }
  return (
    <>
      <b>{priorDelta(delivered.count, delivered.prior)}</b> {priorLabel(days)}
    </>
  );
};

const Tiles: React.FC<{ tiles: Tile[]; className: string; dim: boolean }> = ({
  tiles,
  className,
  dim,
}) => (
  <div className={`kpi-strip ${className} ${dim ? 'is-refreshing' : ''}`}>
    {tiles.map((tile) => (
      <div className={`kpi ${tile.warn ? 'kpi--warn' : ''}`} key={tile.key}>
        <div className="kpi-label">{tile.label}</div>
        <div className="kpi-value">{tile.value ?? '—'}</div>
        {tile.sub ? <div className="kpi-sub">{tile.sub}</div> : null}
      </div>
    ))}
  </div>
);

// The CalendarFilter speaks 'YYYY-MM-DD <> YYYY-MM-DD'; the URL and API use from/to.
const calendarValue = (range: SummaryRange) =>
  range.from && range.to ? `${range.from} <> ${range.to}` : '';

const rangeFromCalendar = (value: unknown): SummaryRange => {
  if (typeof value === 'string' && value.includes('<>')) {
    const [from, to] = value.split('<>').map((part) => part.trim());
    return parseRange(`${from}..${to}`);
  }
  return { preset: DEFAULT_RANGE };
};

// Dashboard KPIs. "Right now" is the backlog by status. The other group is received and
// delivered over a range picked here (presets or a custom calendar range) and kept in ?range=
// so the view survives a refresh and can be linked. The days shown next to the title are the
// ones the API actually computed over. If the request fails, the backlog tiles fall back to
// the sidebar's section counts and the range tiles show no numbers.
const KpiStrip: React.FC<{ counts?: Counts }> = ({ counts }) => {
  const [range, setRange] = useState<SummaryRange>(() =>
    parseRange(initialParams().get('range')),
  );
  useUrlFilters({
    range: {
      kind: 'text',
      value: rangeToParam(range),
      set: (raw: string) => setRange(parseRange(raw)),
    },
  });

  const { data: summary, isFetching } = useOrderSummary(range);
  const dim = isFetching && Boolean(summary);
  const isCustom = Boolean(range.from && range.to);

  const now = summary?.now;
  const nowTiles: Tile[] = now
    ? [
        { key: 'new', label: 'New orders', value: now.newOrders.count },
        {
          key: 'kitting',
          label: 'Awaiting kitting',
          value: now.awaitingKitting.count,
          warn: (now.awaitingKitting.oldestDays ?? 0) >= 2,
          sub:
            now.awaitingKitting.oldestDays != null
              ? `oldest ${plural(now.awaitingKitting.oldestDays, 'day')}`
              : null,
        },
        {
          key: 'shipping',
          label: 'In shipping',
          value: now.inShipping.count,
          sub: `${now.inShipping.inProgress} in progress`,
        },
        {
          key: 'transit',
          label: 'In transit',
          value: now.inTransit.count,
          sub:
            now.inTransit.avgDays != null
              ? `avg ${now.inTransit.avgDays} days so far`
              : null,
        },
      ]
    : [
        {
          key: 'new',
          label: 'New orders',
          value: counts?.[pagesSlug.newOrders],
        },
        {
          key: 'kitting',
          label: 'Awaiting kitting',
          value: counts?.[pagesSlug.kitting],
        },
        {
          key: 'shipping',
          label: 'In shipping',
          value: counts?.[pagesSlug.shipping],
        },
        {
          key: 'delivery',
          label: 'In delivery',
          value: counts?.[pagesSlug.delivered],
        },
      ];

  const days = summary?.range.days ?? 0;
  const received = summary?.received;
  const delivered = summary?.delivered;
  const rangeTiles: Tile[] = [
    {
      key: 'received',
      label: 'Received',
      value: received?.count,
      sub: received ? (
        <>
          <b>{priorDelta(received.count, received.prior)}</b> {priorLabel(days)}
        </>
      ) : null,
    },
    {
      key: 'delivered',
      label: 'Delivered',
      value: delivered?.count,
      sub: deliveredSub(delivered, days),
    },
    {
      key: 'to-door',
      label: 'Avg days to deliver',
      value: delivered?.avgDaysToDeliver,
      sub: delivered ? 'order to delivered' : null,
    },
  ];

  const dates = summary
    ? formatDateRange(summary.range.from, summary.range.to)
    : '';

  return (
    <div className="kpi-board">
      <div className="kpi-head">
        <span className="kpi-title">Right now</span>
      </div>
      <div className="kpi-head kpi-head--range">
        <span className="kpi-title">{rangeTitle(range)}</span>
        {dates ? <span className="kpi-dates">{dates}</span> : null}
        <div className="kpi-controls">
          <div className="kpi-presets" role="group" aria-label="Summary range">
            {RANGE_PRESETS.map((preset) => {
              const active = !isCustom && range.preset === preset.key;
              return (
                <button
                  type="button"
                  key={preset.key}
                  className={active ? 'is-active' : ''}
                  aria-pressed={active}
                  title={preset.title}
                  onClick={() => setRange({ preset: preset.key })}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          <div className="kpi-custom" title="Custom range">
            <CalendarFilter
              key={isCustom ? 'custom' : 'preset'}
              selectedDate={calendarValue(range)}
              setSelectedDate={(value) => setRange(rangeFromCalendar(value))}
              isClearFilter={false}
            />
          </div>
        </div>
      </div>
      <Tiles tiles={nowTiles} className="kpi-strip--now" dim={dim} />
      <Tiles tiles={rangeTiles} className="kpi-strip--range" dim={dim} />
    </div>
  );
};

export default KpiStrip;
